import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail, apiErrorCode } from "@/lib/api";
import { LOGIN } from "@/constants/testIds";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setNeedsVerification(false);
    try {
      const user = await login(email, password);
      toast.success(`Bienvenido, ${user.name}`);
      navigate(user.role === "admin" ? "/admin" : user.role === "super_admin" ? "/superadmin" : "/portal");
    } catch (err) {
      if (apiErrorCode(err) === "EMAIL_NOT_VERIFIED") {
        setNeedsVerification(true);
      } else {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      const { data } = await api.post("/auth/resend-verification", { email });
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8 justify-center">
          <ShieldCheck className="w-7 h-7 text-petroleum" strokeWidth={1.5} />
          <span className="font-heading text-xl font-bold text-charcoal">CERH</span>
        </Link>
        <div className="bg-card border border-petroleum/10 rounded-2xl p-8 shadow-[0_8px_30px_rgba(14,79,79,0.08)]">
          <h1 className="font-heading text-2xl font-bold text-charcoal mb-1">Iniciar sesión</h1>
          <p className="text-sm text-charcoal/60 mb-6">Administradores y empleados usan el mismo acceso.</p>

          {needsVerification && (
            <div className="bg-accent border border-terracotta/30 rounded-xl p-4 text-sm mb-4">
              <p className="text-charcoal/80 mb-2">Debes confirmar tu correo antes de ingresar.</p>
              <button data-testid="resend-verification-button" onClick={resend} className="text-petroleum font-semibold underline">Reenviar correo de confirmación</button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Correo</Label>
              <Input data-testid={LOGIN.emailInput} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@empresa.mx" />
            </div>
            <div>
              <Label>Contraseña</Label>
              <Input data-testid={LOGIN.passwordInput} type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
            </div>
            <Button data-testid={LOGIN.submitButton} type="submit" disabled={loading} className="w-full bg-petroleum hover:bg-petroleum/90 rounded-full h-11">
              {loading ? "Ingresando..." : "Ingresar"}
            </Button>
          </form>
          <button disabled className="text-xs text-charcoal/40 w-full text-center mt-4 cursor-not-allowed" data-testid="login-google-button-stub">
            Iniciar sesión con Google (próximamente)
          </button>
          <p className="text-sm text-center text-charcoal/60 mt-4">
            ¿No tienes cuenta? <Link data-testid={LOGIN.registerLink} to="/registro" className="text-petroleum font-semibold">Registra tu empresa</Link>
          </p>
          <Link data-testid={LOGIN.forgotPasswordLink} to="/olvide-contrasena" className="block text-center text-xs text-charcoal/50 mt-3 underline">¿Olvidaste tu contraseña?</Link>
        </div>
      </div>
    </div>
  );
}
